import { useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store/appStore'
import { fetchCurrentUser } from '@/api/currentUserApi'

export function OfflineSessionExpiredOverlay() {
  const { t } = useTranslation()
  const offlineSessionExpired = useAppStore((s) => s.offlineSessionExpired)
  const isTrulyOffline = useAppStore((s) => s.isTrulyOffline)
  const setManualOffline = useAppStore((s) => s.setManualOffline)
  const setOfflineSessionExpired = useAppStore((s) => s.setOfflineSessionExpired)
  const setOfflineSessionStart = useAppStore((s) => s.setOfflineSessionStart)

  const [isChecking, setIsChecking] = useState(false)
  const [checkFailed, setCheckFailed] = useState(false)

  if (!offlineSessionExpired) return null

  const handleCheckNow = async () => {
    setIsChecking(true)
    setCheckFailed(false)
    try {
      await fetchCurrentUser()
      setManualOffline(false)
      setOfflineSessionExpired(false)
      setOfflineSessionStart(null)
    } catch {
      setCheckFailed(true)
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <Box
      role="alertdialog"
      aria-modal="true"
      aria-label={t('offlineSession.expiredOverlayTitle')}
      aria-live="polite"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'var(--color-scrim)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        p: 4,
        color: 'var(--color-white)',
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" fontWeight={700}>
        {t('offlineSession.expiredOverlayTitle')}
      </Typography>
      <Typography variant="body1" sx={{ maxWidth: 480 }}>
        {t('offlineSession.expiredOverlayBody')}
      </Typography>
      {isChecking ? (
        <>
          <CircularProgress size={32} sx={{ color: 'var(--color-white)' }} />
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {t('offlineSession.checkingConnection')}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {isTrulyOffline
              ? t('offlineSession.waitingForNetwork')
              : checkFailed
              ? t('offlineSession.checkFailed')
              : t('offlineSession.waitingForNetwork')}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleCheckNow}
            sx={{
              color: 'var(--color-white)',
              borderColor: 'var(--color-on-dark-50)',
              '&:hover': { borderColor: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' },
            }}
          >
            {t('offlineSession.checkNow')}
          </Button>
        </>
      )}
    </Box>
  )
}
